import React, { useState } from 'react';
import { ImageOff, Sparkles } from 'lucide-react';

interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackText?: string;
  containerClassName?: string;
  aspectRatio?: string;
  showDevError?: boolean;
}

export const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt = 'Adiosoft Görseli',
  className = '',
  containerClassName = '',
  aspectRatio,
  fallbackText,
  showDevError = true,
  ...rest
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleError = () => {
    if (showDevError && import.meta.env?.DEV) {
      console.warn(`[Adiosoft Image Error]: Görsel yüklenemedi: ${src}`);
    }
    setHasError(true);
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  return (
    <div
      className={`relative overflow-hidden ${containerClassName}`}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {/* Loading Skeleton */}
      {isLoading && !hasError && (
        <div className="absolute inset-0 bg-slate-800/80 animate-pulse flex items-center justify-center z-10">
          <div className="w-6 h-6 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin" />
        </div>
      )}

      {/* Fallback Display if image fails to load */}
      {hasError ? (
        <div className="w-full h-full min-h-[140px] bg-gradient-to-br from-slate-900 via-slate-850 to-slate-950 border border-slate-800 flex flex-col items-center justify-center p-4 text-center select-none">
          <div className="w-10 h-10 rounded-xl bg-blue-600/15 border border-blue-500/30 text-blue-400 flex items-center justify-center mb-2 shadow-inner">
            <Sparkles className="w-5 h-5" />
          </div>
          <span className="text-xs font-semibold text-slate-300 line-clamp-1 max-w-[90%]">
            {fallbackText || alt || 'Adiosoft Dijital Projesi'}
          </span>
          <span className="text-[10px] text-slate-500 mt-1">
            Önizleme Güvenli Mod
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={handleError}
          onLoad={handleLoad}
          className={`${className} transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          {...rest}
        />
      )}
    </div>
  );
};
